import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class UGResultsList extends PaginatedList {
  /** @type {((event: Event) => void) | null} */
  #loadMoreClickHandler = null;
  connectedCallback() {
    // 恢复原来的滚动监听功能
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    // 暂时注释掉点击加载更多功能
    // this.#setupLoadMoreButton();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    // 暂时注释掉点击加载更多功能
    // this.#removeLoadMoreButtonListener();
  }

  /**
   * Initialize component without automatic scroll listening
   */
  #initializeComponent() {
    // 只初始化必要的功能，不启动滚动监听
    // 这里可以添加其他必要的初始化逻辑
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['ug-product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`ug-product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('ug-product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-ug-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-ug-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Sets up the load more button click handler
   */
  #setupLoadMoreButton() {
    const { viewMoreNext } = this.refs;
    if (viewMoreNext && viewMoreNext instanceof HTMLButtonElement) {
      this.#loadMoreClickHandler = this.#handleLoadMoreClick.bind(this);
      if (this.#loadMoreClickHandler) {
        viewMoreNext.addEventListener('click', this.#loadMoreClickHandler);
      }
    }
  }

  /**
   * Removes the load more button click handler
   */
  #removeLoadMoreButtonListener() {
    const { viewMoreNext } = this.refs;
    if (viewMoreNext && this.#loadMoreClickHandler) {
      viewMoreNext.removeEventListener('click', this.#loadMoreClickHandler);
    }
  }

  /**
   * Handles the load more button click
   * @param {Event} event
   */
  #handleLoadMoreClick = async (event) => {
    event.preventDefault();

    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;

    // 检查是否还有更多页面
    const currentPage = parseInt(viewMoreNext.dataset.currentPage || '1') || 1;
    const totalPages = parseInt(viewMoreNext.dataset.totalPages || '1') || 1;

    if (currentPage >= totalPages) {
      this.#hideLoadMoreButton();
      return;
    }

    // 显示加载状态
    this.#setLoadingState(true);

    try {
      // 手动触发下一页的渲染
      await this.#loadNextPage();

      // 更新按钮状态
      this.#updateButtonState();

    } catch (error) {
      console.error('加载更多失败:', error);
      this.#showErrorState();
    } finally {
      // 隐藏加载状态
      this.#setLoadingState(false);
    }
  };

  /**
   * Loads the next page manually
   */
  async #loadNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    // 获取当前页码
    const { viewMoreNext } = this.refs;
    const currentPage = parseInt(viewMoreNext?.dataset.currentPage || '1') || 1;
    const nextPage = currentPage + 1;

    // 检查是否还有更多页面
    const totalPages = parseInt(viewMoreNext?.dataset.totalPages || '1') || 1;
    if (nextPage > totalPages) return;

    // 构建下一页的URL
    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage.toString());
    url.hash = '';

    // 获取下一页内容
    const pageContent = await this.#fetchPageContent(url);
    if (!pageContent) return;

    // 解析并添加新内容
    const newElements = this.#parsePageContent(pageContent);
    if (newElements && newElements.length > 0) {
      grid.append(...newElements);

      // 更新URL
      history.pushState('', '', url.toString());
    }
  }

  /**
   * Fetches page content from the server
   * @param {URL} url
   * @returns {Promise<string|null>}
   */
  async #fetchPageContent(url) {
    try {
      const response = await fetch(url.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.text();
    } catch (error) {
      console.error('获取页面内容失败:', error);
      return null;
    }
  }

  /**
   * Parses page content and extracts grid elements
   * @param {string} pageContent
   * @returns {NodeListOf<Element>|null}
   */
  #parsePageContent(pageContent) {
    try {
      const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
      const gridElement = parsedPage.querySelector('[ref="grid"]');
      if (!gridElement) return null;

      return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    } catch (error) {
      console.error('解析页面内容失败:', error);
      return null;
    }
  }

  /**
   * Sets the loading state of the load more button
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext || !(viewMoreNext instanceof HTMLButtonElement)) return;

    if (isLoading) {
      viewMoreNext.classList.add('loading');
      viewMoreNext.disabled = true;
    } else {
      viewMoreNext.classList.remove('loading');
      viewMoreNext.disabled = false;
    }
  }

  /**
   * Updates the button state after loading more content
   */
  #updateButtonState() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;

    const currentPage = parseInt(viewMoreNext.dataset.currentPage || '1') || 1;
    const totalPages = parseInt(viewMoreNext.dataset.totalPages || '1') || 1;

    // 更新当前页码
    viewMoreNext.dataset.currentPage = (currentPage + 1).toString();

    // 检查是否还有更多页面
    if (currentPage + 1 >= totalPages) {
      this.#hideLoadMoreButton();
    }
  }

  /**
   * Hides the load more button
   */
  #hideLoadMoreButton() {
    const { viewMoreNext } = this.refs;
    if (viewMoreNext) {
      viewMoreNext.style.display = 'none';
    }
  }

  /**
   * Shows error state
   */
  #showErrorState() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;

    const textSpan = viewMoreNext.querySelector('.ug-load-more-text');
    if (textSpan) {
      textSpan.textContent = '加载失败，点击重试';
    }
  }
}

if (!customElements.get('ug-results-list')) {
  customElements.define('ug-results-list', UGResultsList);
}
